// pages/stations/RunnerDashboardPage.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, LogOut, Moon, RefreshCw, Sun } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { requestNotificationPermission } from "@/lib/notify";
import { useRealtimeConnection, useRealtimeEvent } from "@/lib/realtime";
import { mergeReadyOrders, replaceStationOrders } from "@/lib/mergeState";
import { localWrite } from "@/lib/eventEngine";
import { EventType } from "@/lib/events";
import { getPresetTones } from "@/lib/chimePresets";
import { MeshStatusPill } from "@/components/MeshStatusPill";
import {
  BACKEND_URL,
  clearSession,
  confirmSession,
  getSession,
  http,
  isTransientCloudError,
  useTheme,
} from "@/app/core";
import { _enqueueChime, _VIBRATE_ORDER_MS, ChimeVolumeControl, playLongWaitChime, unlockAudio } from "@/app/chimes";
import { FullscreenToggleButton, useCoalescedUpdater } from "@/app/fullscreen";


// =============================================================================
// Runner Dashboard — Simplified view showing only ready orders for runners
// to pick up and deliver to tables. Runners can mark items as delivered.
// Optimised for tablets and phones with large tap targets and instant updates.
// =============================================================================

// ---- Runner notification sounds use the same clean engine as all dashboards ----

export function RunnerDashboardPage() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [context, setContext] = useState(null);
  const [orders, setOrdersRaw] = useState([]);
  // Session 72 — ONE SOURCE OF TRUTH (see KitchenDashboardPage): local / WS
  // writes are stamped; a quiet screen lets the server list replace state.
  const lastActivityAtRef = useRef(0);
  const QUIET_BEFORE_AUTHORITATIVE_MS = 2500;
  const setOrders = useCallback((updater) => {
    lastActivityAtRef.current = Date.now();
    setOrdersRaw(updater);
  }, []);
  const [loading, setLoading] = useState(true);
  const [delivering, setDelivering] = useState({});
  // Session 30.22 — Long-wait heads-up (auto, no button on the stations).
  // `longWaits` = orders still being made that already passed the threshold.
  // `wasLongWaitRef` remembers them so the READY card can say "was waiting".
  const [longWaits, setLongWaits] = useState([]);
  const [longWaitThreshold, setLongWaitThreshold] = useState(15);
  const wasLongWaitRef = useRef(new Map()); // orderId → minutes waited when it crossed
  const longWaitAlertedRef = useRef(null);  // null until first fetch seeds silently
  // Session 27.123 — deliveredIdsRef is now a MAP keyed by order id with
  // the timestamp we added it. A previous bug kept ids in a Set
  // forever if a NEW ready item ever landed on the same order (kitchen
  // marks bar-later after runner delivered bar-earlier). The chime
  // would fire — because that logic uses a *separate* alertedOrderIds
  // ref — but the order card would silently vanish because the poll
  // filter still saw the id as "delivered". Auto-expiring after 5 s
  // guarantees the same id can re-enter the ready queue as soon as
  // the backend has committed the delivery.
  const deliveredIdsRef = useRef(new Map());
  // Extended from 5 s → 30 s so a recently-delivered order can't
  // re-appear during brief DB propagation delays.
  const DELIVERED_ID_TTL_MS = 30000;
  // Session 27.118 / 27.125 — Track which order ids we've ALREADY alerted the
  // runner about. Changed from a Set<orderId> to a Map<orderId, fingerprint>
  // where fingerprint = count of ready items. This way, when ADDITIONAL items
  // in the same order become ready (e.g. staff sends kitchen items first, bar
  // items later), the fingerprint changes and a NEW chime fires.
  const alertedOrderIdsRef = useRef(new Map());
  // Session 27.118 — On first mount we seed the alerted set with
  // whatever ready orders already exist so we don't spam-chime
  // through a stack of pre-existing ready tickets the runner opened
  // to.  Set on the first successful fetch.
  const alertBaselineReadyRef = useRef(false);
  // Concurrency guard — prevents multiple overlapping /staff/orders
  // requests. On slow production networks (>2 s round-trip) the old
  // sequence-counter approach caused starvation: the counter kept
  // incrementing faster than responses arrived, so NO response was
  // ever treated as "current" and lastFetchAt never got set →
  // the runner stayed on "Connecting…" forever.  The in-flight flag
  // is simpler and correct: polling skips if a fetch is already in
  // progress; WS-triggered fetches are flagged as forced so they
  // always run (and the next poll waits until they're done).
  const fetchInFlightRef = useRef(false);

  const session = getSession("staff");
  const authHeaders = useMemo(() => session?.token ? { Authorization: `Bearer ${session.token}` } : {}, [session?.token]);

  // Session 27.118 — Runner alert helper. Plays the SAME kitchen-style
  // chime as Kitchen/Bar (through the shared chime queue), fires a
  // system-level browser Notification so the runner sees a banner even
  // when the tab is in the background / phone screen was locked, and
  // vibrates. Runs *independently* of the Staff dashboard's notification
  // toggle — the runner ALWAYS gets a ping when kitchen or bar sends
  // something out.
  const fireRunnerAlert = useCallback((title, body) => {
    // Session 27.126 — Chime + vibrate only. Removed toast pop-ups
    // and system Notifications per user request — the order card
    // appearing on screen is sufficient visual feedback.
    try {
      const tones = getPresetTones("kitchen");
      _enqueueChime({ tones, vibrate: true, vibrateMs: _VIBRATE_ORDER_MS });
    } catch (_) { /* noop */ }
  }, []);

  // Load context on mount.
  useEffect(() => {
    if (!session?.token) { navigate("/runner", { replace: true }); return; }
    confirmSession("staff");
    // Unlock AudioContext so chimes can play on mobile (requires user gesture).
    unlockAudio();
    const onGesture = () => { unlockAudio(); };
    window.addEventListener("pointerdown", onGesture, { once: true, capture: true });
    window.addEventListener("keydown", onGesture, { once: true, capture: true });
    // Session 27.118 — Politely ask for system Notification permission
    // so we can pop a background-safe banner when kitchen/bar send an
    // order out. Silently no-ops on iOS Safari (unsupported), which is
    // fine — the in-app toast + chime + vibration still fires there.
    try { requestNotificationPermission().catch(() => {}); } catch (_) { /* noop */ }
    http.get("/auth/me", { headers: authHeaders })
      .then((r) => {
        setContext(r.data);
      })
      .catch(() => { navigate("/runner", { replace: true }); });
    return () => {
      window.removeEventListener("pointerdown", onGesture, { capture: true });
      window.removeEventListener("keydown", onGesture, { capture: true });
    };
  }, [session?.token, authHeaders, navigate]);

  // Connect to WebSocket for instant order updates.
  useRealtimeConnection({ baseUrl: BACKEND_URL, token: session?.token });
  const queueOrdersUpdate = useCoalescedUpdater(setOrders);   // Session 70.E — one render per WS burst
  useRealtimeEvent(["hello", "state_changed", "order_status_changed"], (msg) => {
    // Session 28 — Event-driven state architecture for Runner.
    // WS events are the PRIMARY update channel. Direct state mutations
    // happen here; HTTP fetches only occur on gaps/reconciliation.
    try {
      // Session 30 — Fresh connection (or reconnect after a dead socket
      // was culled): resync the dedupe counter to the server's current
      // seq and reconcile immediately to catch anything missed.
      if ((msg?.event_type || msg?.type) === "hello") {
        const serverSeq = Number((msg?.payload || {}).seq);
        runnerLastSeqRef.current = Number.isFinite(serverSeq) ? serverSeq : 0;
        scheduleRunnerReconciliation();
        return;
      }
      // Session 72 — any write anywhere → one debounced reconcile.
      if ((msg?.event_type || msg?.type) === "state_changed") {
        if (typeof msg?.seq === "number" && msg.seq > runnerLastSeqRef.current) runnerLastSeqRef.current = msg.seq;
        scheduleRunnerReconciliation();
        return;
      }
      const p = msg?.payload || {};
      const orderId = String(p.order_id || p.id || "");
      const seq = typeof msg?.seq === "number" ? msg.seq : 0;

      // Gap detection: if we received a seq higher than lastSeq + 1,
      // we missed event(s). Schedule reconciliation fetch.
      if (seq > 0) {
        const lastSeq = runnerLastSeqRef.current;
        // Dedup: skip events we've already processed
        if (lastSeq > 0 && seq <= lastSeq) {
          return; // Already processed this seq
        }
        if (lastSeq > 0 && seq > lastSeq + 1) {
          console.warn(`[runner-ws] gap detected: last=${lastSeq} received=${seq} — scheduling reconciliation`);
          scheduleRunnerReconciliation();
        }
        runnerLastSeqRef.current = Math.max(runnerLastSeqRef.current, seq);
      }

      // ── Session 31.13 — Void: drop voided lines (or the whole card) ──
      if (p.voided === true && orderId) {
        const voidedIds = new Set((Array.isArray(p.voided_item_ids) ? p.voided_item_ids : []).map(String).filter(Boolean));
        const fullVoid = p.full_void === true || p.status === "voided" || p.status === "cancelled";
        queueOrdersUpdate((prev) => prev.map((o) => {
          if (!o || o.id !== orderId) return o;
          if (fullVoid) return null;
          const items = (o.items || []).filter((it) => it && !it.voided && !voidedIds.has(String(it.id || "")));
          const stillReady = items.some((it) => String(it.status || "").toLowerCase() === "ready");
          if (!stillReady) return null;
          return { ...o, items };
        }).filter(Boolean));
        if (fullVoid) {
          deliveredIdsRef.current.set(orderId, Date.now());
          alertedOrderIdsRef.current.delete(orderId);
        } else {
          scheduleRunnerReconciliation();
        }
        return;
      }

      // ── Ready event: Add/update order card directly from WS payload ──
      if (p.status === "ready" && orderId) {
        // Build order card from enriched WS payload (all_items has every item with status)
        const allItems = Array.isArray(p.all_items) ? p.all_items : [];
        const readyItems = allItems.filter((it) => it && it.status === "ready");
        if (readyItems.length > 0 && !deliveredIdsRef.current.has(orderId)) {
          const wsOrder = {
            id: orderId,
            order_number: p.order_number,
            table_number: p.table_number,
            tag_code: p.tag_code,
            customer_name: p.customer_name,
            status: p.status,
            created_at: p.created_at,
            items: allItems,
            _ws_added_at: Date.now(), // Session 69 — bounds the merge grace (mergeReadyOrders)
          };
          queueOrdersUpdate((prev) => {
            // Check for existing order — update or add
            const existingIdx = prev.findIndex((o) => o.id === orderId);
            if (existingIdx >= 0) {
              // Session 30.26 — MERGE items rather than replace: a LAN
              // ping from the kitchen carries only kitchen items and one
              // from the bar only bar items; the cloud event carries all.
              // Union by id (falling back to name+department) and never
              // downgrade a status.
              const rank = { pending: 0, preparing: 0, ready: 1, delivered: 2 };
              const keyOf = (it) => String(it.id || `${it.name}|${it.department}`);
              const merged = new Map((prev[existingIdx].items || []).map((it) => [keyOf(it), it]));
              for (const it of allItems) {
                const k = keyOf(it);
                const cur = merged.get(k);
                if (!cur || (rank[String(it.status || "").toLowerCase()] || 0) >= (rank[String(cur.status || "").toLowerCase()] || 0)) merged.set(k, { ...cur, ...it });
              }
              const updated = [...prev];
              updated[existingIdx] = { ...prev[existingIdx], ...wsOrder, items: Array.from(merged.values()) };
              return updated;
            }
            // New order card
            return [...prev, wsOrder];
          });
          // Fire chime after state mutation (sound + card arrive together)
          const prevFingerprint = alertedOrderIdsRef.current.get(orderId);
          const readyCount = readyItems.length;
          if (alertBaselineReadyRef.current && (prevFingerprint === undefined || readyCount > prevFingerprint)) {
            alertedOrderIdsRef.current.set(orderId, readyCount);
            const heading = p.table_number ? `Table ${p.table_number} — ready` : `Order ${p.order_number || ""} — ready`.trim();
            fireRunnerAlert(heading, "Kitchen or bar sent an order to run.");
          } else if (!alertBaselineReadyRef.current) {
            // First event before baseline — seed fingerprint
            alertedOrderIdsRef.current.set(orderId, readyCount);
          }
        } else if (allItems.length === 0) {
          // Legacy event without enriched all_items — reconcile
          scheduleRunnerReconciliation();
        }
      }

      // ── Delivery/ack/completed: Remove card directly ──
      const isDelivery = (
        p.status === "delivered" ||
        p.status === "completed" ||
        p.acknowledged === "kitchen" ||
        p.acknowledged === "bar"
      );
      if (isDelivery && orderId) {
        queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
        deliveredIdsRef.current.set(orderId, Date.now());
        alertedOrderIdsRef.current.delete(orderId);
      }
      // Session 72 — every status event → one debounced reconcile (see Kitchen).
      scheduleRunnerReconciliation();
    } catch (e) { console.warn("[runner-ws] direct mutation error:", e?.message || e); }
  });

  // Fetch ready orders — called on mount, via polling, and via WS event.
  const [lastFetchAt, setLastFetchAt] = useState(null);
  const [refreshBusy, setRefreshBusy] = useState(false);
  // Session 27.122 — Tick every second so the "last updated" label
  // in the header stays live even between polls. Purely cosmetic —
  // helps runners trust the tablet is talking to the server.
  const [tickNow, setTickNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Session 27.140 — Lock+dirty pattern for Runner (same fix as Staff).
  // Replaces the old fetchReadySeqRef which caused starvation under load.
  const runnerFetchDirtyRef = useRef(false);

  // Session 28 — Sequence tracking + gap-based reconciliation (same
  // architecture as Staff dashboard). WS events are the PRIMARY state
  // update channel; HTTP fetches only happen for initial mount, reconnect,
  // or when a gap in event_seq is detected.
  const runnerLastSeqRef = useRef(0);
  const runnerReconciliationTimerRef = useRef(null);
  const runnerFetchRef = useRef(null); // Points to fetchReadyOrders once defined
  const scheduleRunnerReconciliation = useCallback(() => {
    if (runnerReconciliationTimerRef.current) return;
    runnerReconciliationTimerRef.current = setTimeout(() => {
      runnerReconciliationTimerRef.current = null;
      if (runnerFetchRef.current) runnerFetchRef.current();
    }, 300);
  }, []);
  const fetchReadyOrders = useCallback(async () => {
    // Session 28 — Reconciliation-only fetch. Called on:
    // (a) initial mount, (b) visibility/focus/online recovery,
    // (c) event_seq gap detected, (d) safety-net poll.
    // WS events handle all primary state mutations.
    if (fetchInFlightRef.current) {
      runnerFetchDirtyRef.current = true;
      return;
    }
    fetchInFlightRef.current = true;
    runnerFetchDirtyRef.current = false;
    const fetchStartedAt = Date.now();
    try {
      const r = await http.get("/staff/ready-orders", { headers: authHeaders });
      // Session 30.22 — long-wait heads-up list + threshold.
      try {
        const lw = Array.isArray(r.data.long_waits) ? r.data.long_waits : [];
        if (Number(r.data.long_wait_minutes) > 0) setLongWaitThreshold(Number(r.data.long_wait_minutes));
        lw.forEach((o) => { if (!wasLongWaitRef.current.has(o.id)) wasLongWaitRef.current.set(o.id, o.waiting_minutes || 0); });
        if (longWaitAlertedRef.current === null) {
          longWaitAlertedRef.current = new Set(lw.map((o) => o.id)); // seed silently
        } else {
          const fresh = lw.filter((o) => !longWaitAlertedRef.current.has(o.id));
          lw.forEach((o) => longWaitAlertedRef.current.add(o.id));
          if (fresh.length) {
            try { playLongWaitChime(); } catch (_) { /* noop */ }
            fresh.slice(0, 3).forEach((o) => {
              toast.warning(`Table ${o.table_number || "—"} has waited ${o.waiting_minutes} min — be ready to run`, {
                description: (o.items || []).map((it) => `${it.quantity}× ${it.name}`).join(", "),
                duration: 12000,
              });
            });
          }
        }
        setLongWaits(lw);
      } catch (_) { /* heads-up is best-effort */ }
      // Session 28b — Capture server event_seq for merge, but DO NOT
      // advance runnerLastSeqRef. Only WS events should advance the seq.
      const serverSeq = (typeof r.data.event_seq === "number") ? r.data.event_seq : 0;
      // Prune the delivered map.
      const _nowMs = Date.now();
      deliveredIdsRef.current.forEach((ts, id) => {
        if (_nowMs - ts > DELIVERED_ID_TTL_MS) deliveredIdsRef.current.delete(id);
      });
      // Server response is filtered to ready-only orders.
      const serverReadyOrders = (r.data.orders || []).filter(
        (o) => !deliveredIdsRef.current.has(o.id)
      );
      setLastFetchAt(Date.now());
      // Reconciliation alert logic
      const readyIds = new Set(serverReadyOrders.map((o) => o.id));
      const newOrdersToAlert = [];
      if (!alertBaselineReadyRef.current) {
        for (const o of serverReadyOrders) {
          const readyCount = (o.items || []).filter((i) => i.status === "ready").length;
          alertedOrderIdsRef.current.set(o.id, readyCount);
        }
        alertBaselineReadyRef.current = true;
      } else {
        for (const o of serverReadyOrders) {
          const readyCount = (o.items || []).filter((i) => i.status === "ready").length;
          const prevFingerprint = alertedOrderIdsRef.current.get(o.id);
          if (prevFingerprint === undefined || readyCount > prevFingerprint) {
            alertedOrderIdsRef.current.set(o.id, readyCount);
            newOrdersToAlert.push(o);
          }
        }
      }
      alertedOrderIdsRef.current.forEach((_, id) => {
        if (!readyIds.has(id)) alertedOrderIdsRef.current.delete(id);
      });
      // Session 72 — quiet screen → server list replaces; busy → monotonic merge.
      const authoritative = fetchStartedAt - (lastActivityAtRef.current || 0) > QUIET_BEFORE_AUTHORITATIVE_MS;
      setOrdersRaw((prev) => (
        authoritative
          ? replaceStationOrders(prev, serverReadyOrders, deliveredIdsRef.current)
          : mergeReadyOrders(prev, serverReadyOrders, serverSeq, runnerLastSeqRef.current, deliveredIdsRef.current)
      ));
      for (const o of newOrdersToAlert) {
        const heading = o.table_number ? `Table ${o.table_number} — ready` : `Order ${o.order_number || ""} — ready`.trim();
        fireRunnerAlert(heading, "Kitchen or bar sent an order to run.");
      }
    } catch (err) {
      console.warn("[Runner] fetchReadyOrders failed:", err?.message || err);
    }
    finally {
      fetchInFlightRef.current = false;
      setLoading(false);
      if (runnerFetchDirtyRef.current) {
        runnerFetchDirtyRef.current = false;
        setTimeout(() => fetchReadyOrders(), 50);
      }
    }
  }, [authHeaders, fireRunnerAlert]);
  runnerFetchRef.current = fetchReadyOrders;

  // Session 28 — Safety-net poll only. Primary updates come from WS.
  // Interval bumped from 3s → 10s since WS handles the fast path.
  useEffect(() => {
    if (!session?.token) return;
    fetchReadyOrders();
    const RECONCILIATION_INTERVAL = 10000;
    const interval = setInterval(fetchReadyOrders, RECONCILIATION_INTERVAL);
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        fetchReadyOrders();
      }
    };
    const onFocus = () => { fetchReadyOrders(); };
    const onOnline = () => { fetchReadyOrders(); };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
      window.addEventListener("online", onOnline);
    }
    return () => {
      clearInterval(interval);
      if (runnerReconciliationTimerRef.current) {
        clearTimeout(runnerReconciliationTimerRef.current);
        runnerReconciliationTimerRef.current = null;
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
        window.removeEventListener("online", onOnline);
      }
    };
  }, [session?.token, fetchReadyOrders]);

  // Optimistic mark-delivered — remove card immediately, track ID to prevent reappearing.
  const markDelivered = async (orderId) => {
    // Session 27.123 — Stamp with current timestamp so the entry
    // auto-expires after DELIVERED_ID_TTL_MS. Prevents a permanent
    // filter that would silently swallow a follow-up ready item on
    // the same order.
    deliveredIdsRef.current.set(orderId, Date.now());
    // Optimistically remove the order from the list for instant feedback.
    setOrders((prev) => prev.filter((o) => o.id !== orderId));
    setDelivering((prev) => ({ ...prev, [orderId]: true }));
    try {
      await http.post(`/staff/orders/${orderId}/deliver-ready-items`, {}, { headers: authHeaders });
      wasLongWaitRef.current.delete(orderId); // Session 30.22 — heads-up cycle complete
      // Session 27.126 — Removed "Delivered!" toast per user request.
      // The card disappearing is sufficient visual feedback.
    } catch (error) {
      // Session 30.26 — Router-only operation: the REST call cannot
      // reach the cloud (or the order is still a LAN-optimistic
      // `local-…` ticket). Queue the delivery as an offline event — it
      // travels to the Coordinator over the LAN now and to the cloud
      // when the internet returns (the backend resolves optimistic ids
      // and defers until the station's READY has landed).
      // Session 54 — 5xx / 503 / Cloudflare edge errors are offline-like too:
      // the runner DID deliver; the cloud just couldn't record it yet.
      const offlineLike = isTransientCloudError(error) || String(orderId).startsWith("local-");
      if (offlineLike) {
        try {
          const card = orders.find((o) => o.id === orderId) || null;
          // Departments whose items were READY on the card when the
          // runner delivered — lets the server wait for those stations'
          // own READY events to land before applying the delivery.
          const deliveredDepartments = Array.from(new Set(
            (card?.items || [])
              .filter((it) => it && String(it.status || "").toLowerCase() === "ready")
              .map((it) => String(it.department || "").trim().toLowerCase())
              .filter(Boolean)
          ));
          await localWrite({
            type: EventType.ORDER_STATUS_CHANGED,
            payload: {
              order_id: orderId,
              order_change_id: card?._change_id || null,
              status: "delivered",
              delivered_at: new Date().toISOString(),
              delivered_departments: deliveredDepartments,
              order_number: card?.order_number || null,
              table_number: card?.table_number || null,
            },
            session,
          });
          wasLongWaitRef.current.delete(orderId);
          toast.message("Offline — delivery saved locally and will sync automatically.");
          return;
        } catch (_) { /* fall through to the revert path */ }
      }
      toast.error("Unable to mark as delivered — will retry.");
      // Remove from delivered map so it reappears.
      deliveredIdsRef.current.delete(orderId);
      fetchReadyOrders();
    }
    finally { setDelivering((prev) => ({ ...prev, [orderId]: false })); }
  };

  const handleLogout = async () => {
    try { await http.post("/auth/logout", {}, { headers: authHeaders }); } catch (_) {}
    clearSession("staff", "user_logout");
    navigate("/runner", { replace: true });
  };

  // Session 27.118 — Mirror the ready-order count into document.title
  // so a runner with the phone locked or the tab in background still
  // sees the count on the browser tab bar / lock-screen preview.
  // Restores the original title when the count drops to zero (or on
  // unmount) so we don't clobber other pages.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const original = document.title;
    if (orders.length > 0) {
      document.title = `(${orders.length}) Runner — ready`;
    } else {
      document.title = "Runner";
    }
    return () => { document.title = original; };
  }, [orders.length]);

  const businessName = context?.business?.name || "Restaurant";
  const isDark = theme === "dark";

  return (
    <div className="min-h-screen bg-background" data-testid="runner-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="runner-heading">Runner</h1>
            <p className="text-sm text-muted-foreground truncate">{businessName}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Session 30.33 — LAN / Cloud mesh pill, same as Kitchen, Bar,
                Staff and Retail, so runners can see which devices they are
                connected to and rescan when the router-only mesh drops. */}
            <MeshStatusPill />
            {/* Dark/Light mode toggle — Session 30.4: outlined icon box,
                identical to the Kitchen / Bar station header. */}
            <Button variant="outline" size="icon" className="h-10 w-10 rounded-xl" onClick={() => setTheme(isDark ? "light" : "dark")} data-testid="runner-theme-toggle" aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"} title={isDark ? "Switch to light mode" : "Switch to dark mode"}>
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            {/* Full-screen toggle — added Session 27.114 so runners can
                use the entire tablet screen for the ready-queue. */}
            <FullscreenToggleButton className="h-10 w-10" />
            {/* Session 27.115 — Unified sound settings popover (same
                as Kitchen/Bar). Contains volume slider, preset picker,
                mute toggle and Play-test button. Replaces the previous
                Bell notifications toggle so operators have ONE place
                to control every audio setting on the tablet.
                Session 27.116 — outline variant so the trigger has a
                visible box like the fullscreen toggle beside it,
                instead of only appearing on click. */}
            <ChimeVolumeControl role="runner" variant="outline" />
            <Button variant="outline" className="rounded-xl border-destructive/40 text-destructive hover:border-destructive hover:bg-destructive/10 hover:text-destructive" onClick={handleLogout} data-testid="runner-logout-btn">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Ready orders list */}
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-semibold" data-testid="runner-section-heading">Ready for pickup</h2>
          <div className="flex items-center gap-2">
            {/* Session 27.122 — Manual refresh + "last updated" so
                runners always have a visible escape hatch when the
                background poll feels unresponsive, and a live signal
                that the tablet is talking to the server. */}
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-9 gap-1.5 px-2.5"
              onClick={async () => {
                setRefreshBusy(true);
                try { await fetchReadyOrders(); } catch (_) {}
                setTimeout(() => setRefreshBusy(false), 350);
              }}
              disabled={refreshBusy}
              data-testid="runner-refresh-btn"
              title="Refresh now"
            >
              <RefreshCw className={`h-4 w-4 ${refreshBusy ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline text-sm">Refresh</span>
            </Button>
            <Badge variant="secondary" className="rounded-full text-sm px-3 py-1 font-semibold" data-testid="runner-order-count">{orders.length}</Badge>
          </div>
        </div>
        {/* Session 27.126 — Removed "Live sync active" indicator per
            user request. Sync runs silently in the background. */}

        {/* Session 30.22 — Heads-up: tables whose orders passed the long-wait
            threshold but are NOT ready yet. Appears automatically, stays until
            the order turns ready (it then shows below with a "was waiting"
            badge) or is delivered. */}
        {longWaits.length > 0 ? (
          <section
            className="mb-4 rounded-[24px] border-2 border-rose-400/70 bg-rose-50/90 p-4 dark:border-rose-700/70 dark:bg-rose-950/40 animate-[nfcsLongWaitPulse_1.6s_ease-in-out_infinite]"
            role="status"
            aria-live="polite"
            data-testid="runner-long-wait-panel"
          >
            <style>{`@keyframes nfcsLongWaitPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(244,63,94,0.45); } 50% { box-shadow: 0 0 0 10px rgba(244,63,94,0); } }`}</style>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 text-base font-semibold text-rose-900 dark:text-rose-100">
                <AlertTriangle className="h-5 w-5 animate-pulse" /> Heads up — be ready to run
              </h3>
              <span className="rounded-full bg-rose-600 px-2.5 py-0.5 text-xs font-bold text-white" data-testid="runner-long-wait-count">
                {longWaits.length} waiting over {longWaitThreshold} min
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {longWaits.map((o) => (
                <div key={o.id} className="rounded-2xl border border-rose-300/80 bg-white/80 p-3 dark:border-rose-800/70 dark:bg-rose-950/50" data-testid={`runner-long-wait-${o.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-bold text-rose-950 dark:text-rose-50">
                      Table {o.table_number || "—"}
                      {o.order_number ? <span className="ml-1.5 font-medium text-rose-800/80 dark:text-rose-200/80">· {o.order_number}</span> : null}
                    </p>
                    <span className="shrink-0 rounded-full bg-rose-600 px-2 py-0.5 text-xs font-bold tabular-nums text-white" data-testid={`runner-long-wait-minutes-${o.id}`}>{o.waiting_minutes} min</span>
                  </div>
                  <ul className="mt-1 space-y-0.5 text-sm text-rose-950/90 dark:text-rose-100/90">
                    {(o.items || []).map((it, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="min-w-0 truncate"><span className="font-semibold tabular-nums">{it.quantity}×</span> {it.name}</span>
                        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide opacity-70">{it.status === "preparing" ? "cooking" : it.status}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-[11px] text-rose-900/70 dark:text-rose-200/70">Still being made{o.ready_items ? ` · ${o.ready_items} item${o.ready_items === 1 ? "" : "s"} already ready below` : ""} — you'll be told the moment it's ready.</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-36 w-full rounded-2xl" />
            <Skeleton className="h-36 w-full rounded-2xl" />
            <Skeleton className="h-36 w-full rounded-2xl" />
          </div>
        ) : orders.length === 0 ? (
          <Card className="rounded-[24px] border-border bg-card" data-testid="runner-empty-state">
            <CardContent className="flex flex-col items-center justify-center py-20 sm:py-24 text-center">
              <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-emerald-500/10">
                <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold">{longWaits.length ? "Nothing ready yet" : "All caught up!"}</h3>
              <p className="mt-2 text-base text-muted-foreground max-w-sm">{longWaits.length ? "The tables above are running late — stay close, they will appear here the moment they are ready." : "No orders ready for pickup right now. New orders will appear here instantly."}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="runner-orders-list">
            {orders.map((order) => {
              const readyItems = (order.items || []).filter((i) => i.status === "ready");
              const tableNum = order.table_number || order.tag_code || "—";
              const customerName = order.customer_name || "";

              // Determine the station(s) for this card so we can colour it.
              const depts = new Set(readyItems.map((i) => {
                const d = (i.department || "kitchen").toString().trim().toLowerCase();
                return (d === "bar" || d === "drinks" || d === "beverages") ? "bar" : "kitchen";
              }));
              const isAllBar = depts.size === 1 && depts.has("bar");
              const isAllKitchen = !isAllBar && (depts.size === 0 || (depts.size === 1 && depts.has("kitchen")));

              // Card wrapper — full background tint by station.
              const cardClass = isAllBar
                ? "rounded-[24px] border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/40 overflow-hidden transition-all duration-200"
                : isAllKitchen
                ? "rounded-[24px] border-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 overflow-hidden transition-all duration-200"
                : "rounded-[24px] border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/40 overflow-hidden transition-all duration-200";

              // Header strip colour.
              const headerClass = isAllBar
                ? "flex items-center justify-between border-b border-blue-200 dark:border-blue-800 px-5 py-3 sm:px-6 bg-blue-500/20"
                : isAllKitchen
                ? "flex items-center justify-between border-b border-emerald-200 dark:border-emerald-800 px-5 py-3 sm:px-6 bg-emerald-500/20"
                : "flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-5 py-3 sm:px-6 bg-slate-500/10";

              // Deliver button colour.
              const deliverClass = isAllBar
                ? "w-full h-14 sm:h-16 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-base sm:text-lg font-bold gap-2.5 active:scale-[0.98] transition-transform"
                : "w-full h-14 sm:h-16 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-base sm:text-lg font-bold gap-2.5 active:scale-[0.98] transition-transform";

              // Station label shown in the header.
              const stationLabel = isAllBar ? "Bar" : isAllKitchen ? "Kitchen" : "Kitchen & Bar";
              return (
                <Card key={order.id} className={cardClass} data-testid={`runner-order-${order.id}`}>
                  <CardContent className="p-0">
                    <div className={headerClass}>
                      <div className="flex flex-wrap items-center gap-3">
                        <Badge variant="default" className="rounded-full font-bold text-base sm:text-lg px-4 py-1">Table {tableNum}</Badge>
                        {customerName && <span className="text-sm text-muted-foreground">{customerName}</span>}
                        {wasLongWaitRef.current.has(order.id) ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white animate-pulse" data-testid={`runner-was-long-wait-${order.id}`}>
                            <AlertTriangle className="h-3.5 w-3.5" /> Waited {Math.max(wasLongWaitRef.current.get(order.id) || 0, longWaitThreshold)}+ min — go now
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-muted-foreground">{stationLabel}</span>
                        <span className="text-sm text-muted-foreground">·</span>
                        <span className="text-sm text-muted-foreground font-medium">{readyItems.length} item{readyItems.length !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <div className="px-5 py-4 sm:px-6 space-y-2.5">
                      {readyItems.map((item, idx) => {
                        // Session 27.123 — colour-coded dept badge so runners
                        // can tell at a glance whether the ticket needs a
                        // stop at the bar (blue) or the kitchen (green).
                        const deptRaw = (item.department || "kitchen").toString().trim().toLowerCase();
                        const isBar = deptRaw === "bar" || deptRaw === "drinks" || deptRaw === "beverages";
                        const deptClass = isBar
                          ? "border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300"
                          : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
                        return (
                        <div key={item.id || `order-item-${idx}`} className="flex items-center justify-between">
                          <span className="text-base sm:text-lg font-medium">{item.quantity > 1 ? `${item.quantity}× ` : ""}{item.name}</span>
                          <Badge variant="outline" className={`rounded-full text-xs sm:text-sm capitalize px-2.5 py-0.5 ${deptClass}`}>{item.department || "kitchen"}</Badge>
                        </div>
                        );
                      })}
                    </div>
                    <div className="border-t border-border/40 px-5 py-4 sm:px-6">
                      <Button
                        className={deliverClass}
                        disabled={!!delivering[order.id]}
                        onClick={() => markDelivered(order.id)}
                        data-testid={`runner-deliver-${order.id}`}
                      >
                        {delivering[order.id] ? "Marking…" : (<><CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6" /> Mark delivered</>)}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
